import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from './contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';

import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Layouts
import DashboardLayout from './layouts/DashboardLayout';
import AuthLayout from './layouts/AuthLayout';

// Pages
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import ShopifyConfig from './pages/ShopifyConfig';
import ErpConfig from './pages/ErpConfig';
import ProductSync from './pages/ProductSync';
import CustomerSync from './pages/CustomerSync';
import OrderSync from './pages/OrderSync';
import TrackingSync from './pages/TrackingSync';
import SyncLogs from './pages/SyncLogs';
import Schedulers from './pages/Schedulers';
import Users from './pages/Users';
import Profile from './pages/Profile';
import PricingSync from './pages/PricingSync';

import './styles/index.css';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  }
});

// Protected route component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to='/dashboard' replace />;
  }

  return children;
};

// Create router with direct route definitions
const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    children: [
      { path: 'login', element: <Login /> }
    ]
  },
  {
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to='dashboard' replace /> },
      { path: 'dashboard', element: <Dashboard /> },
      { path: 'shopify-config', element: <ShopifyConfig /> },
      { path: 'erp-config', element: <ErpConfig /> },
      {
        path: 'product-sync',
        children: [
          { index: true, element: <Navigate to='operations' replace /> },
          { path: 'operations', element: <ProductSync /> },
          { path: 'products', element: <ProductSync /> },
          { path: 'mapping', element: <ProductSync /> },
          { path: 'settings', element: <ProductSync /> }
        ]
      },
      {
        path: 'customer-sync',
        children: [
          { index: true, element: <Navigate to='shopify-to-erp/operations' replace /> },
          {
            path: 'shopify-to-erp',
            children: [
              { index: true, element: <Navigate to='operations' replace /> },
              { path: 'operations', element: <CustomerSync /> },
              { path: 'customers', element: <CustomerSync /> },
              { path: 'mapping', element: <CustomerSync /> },
              { path: 'settings', element: <CustomerSync /> }
            ]
          },
          {
            path: 'erp-to-shopify',
            children: [
              { index: true, element: <Navigate to='operations' replace /> },
              { path: 'operations', element: <CustomerSync /> },
              { path: 'customers', element: <CustomerSync /> },
              { path: 'mapping', element: <CustomerSync /> },
              { path: 'settings', element: <CustomerSync /> }
            ]
          },
          // Legacy routes for backward compatibility
          { path: 'operations', element: <Navigate to='shopify-to-erp/operations' replace /> },
          { path: 'customers', element: <Navigate to='shopify-to-erp/customers' replace /> },
          { path: 'mapping', element: <Navigate to='shopify-to-erp/mapping' replace /> },
          { path: 'settings', element: <Navigate to='shopify-to-erp/settings' replace /> }
        ]
      },
      {
        path: 'pricing-sync',
        children: [
          { index: true, element: <Navigate to='operations' replace /> },
          { path: 'operations', element: <PricingSync /> },
          { path: 'pricing', element: <PricingSync /> },
          { path: 'mapping', element: <PricingSync /> },
          { path: 'settings', element: <PricingSync /> }
        ]
      },
      {
        path: 'order-sync',
        children: [
          { index: true, element: <Navigate to='orders' replace /> },
          { path: 'orders', element: <OrderSync /> },
          { path: 'settings', element: <OrderSync /> }
        ]
      },
      {
        path: 'tracking-sync',
        children: [
          { index: true, element: <Navigate to='operations' replace /> },
          { path: 'operations', element: <TrackingSync /> },
          { path: 'tracking', element: <TrackingSync /> },
          { path: 'mapping', element: <TrackingSync /> },
          { path: 'settings', element: <TrackingSync /> }
        ]
      },
      { path: 'sync-logs', element: <SyncLogs /> },
      { path: 'schedulers', element: <Schedulers /> },
      {
        path: 'users',
        element: (
          <ProtectedRoute requiredRole='admin'>
            <Users />
          </ProtectedRoute>
        )
      },
      { path: 'profile', element: <Profile /> }
    ]
  },
  { path: '*', element: <NotFound /> }
], {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  },
  basename: '/'
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <CssBaseline />
          <RouterProvider
            router={router}
            fallbackElement={<div>Loading...</div>}
          />
          <ToastContainer position='top-right' autoClose={5000} />
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
